import React, {
  createContext,
  type Dispatch,
  type FC,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useTemplatesData } from '../hooks/useTemplatesData';
import { usePageNavigate } from '../hooks/usePageNavigate';
import type { AppTemplate } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { ActiveModal } from '../components/TemplatePage/Modals/ModalRenderer';
import { useEssentials } from '@wix/fe-essentials-standalone';

export interface SingleTemplateContextProps {
  template: AppTemplate;
  activeModal: ActiveModal;
  setActiveModal: Dispatch<SetStateAction<ActiveModal>>;
}

export const SingleTemplateContext = createContext<SingleTemplateContextProps>({
  template: null,
  activeModal: ActiveModal.NONE,
  setActiveModal: () => {},
});
export const SingleTemplateContextProvider: FC = ({ children }) => {
  const { templates } = useTemplatesData();
  const [currentTemplate, setCurrentTemplate] = useState<AppTemplate | null>(
    null,
  );
  const [activeModal, setActiveModal] = React.useState<ActiveModal>(
    ActiveModal.NONE,
  );
  const { panoramaClient } = useEssentials();
  const { templateId, navigateToMainPage } = usePageNavigate();

  useEffect(() => {
    const navigateToMainPageOnFirstRender = () => {
      panoramaClient
        .errorMonitor()
        .reportError(
          new Error(
            'Navigate to main page on first render of app single template',
          ),
          { context: { templateId } },
        );
      navigateToMainPage();
    };

    if (!templateId) {
      navigateToMainPageOnFirstRender();
    }

    const matchedTemplate = templates.find(
      (template) => template.id === templateId,
    );
    const shouldNavigateToMainPage =
      templates?.length && (!matchedTemplate || !matchedTemplate?.previewUrl);

    if (shouldNavigateToMainPage) {
      navigateToMainPageOnFirstRender();
    } else {
      setCurrentTemplate(matchedTemplate);
    }
  }, [templates, templateId]);

  return (
    <SingleTemplateContext.Provider
      value={{ template: currentTemplate, activeModal, setActiveModal }}
    >
      {children}
    </SingleTemplateContext.Provider>
  );
};
